/** Path prefixes that indicate we're on a revision-related page (so we keep sticky lock during transition). */
const REVISION_PATH_PREFIXES = [
  "/revision-submission/",
  "/revision/",
  "/view-revision/",
  "/view-section/",
  "/view-step/",
  "/view-substep/",
  "/view-childnode/",
  "/approve-revision/",
  "/release-revision/",
  "/released-revision/",
  "/rejected-revision/",
  "/archived-revision/",
  "/add-new-section/",
  "/edit-section/",
  "/add-new-step/",
  "/edit-step/",
  "/add-new-substep/",
  "/edit-substep/",
  "/add-new-attachment/",
  "/edit-attachment/",
  "/add-new-video/",
  "/edit-video/",
  "/add-new-richtext/",
  "/edit-rich-text/",
  "/add-new-canvas/",
  "/edit-canvas-new/",
  "/edit-canvas/",
  "/canvas",
  "/edit-procedure-revision/",
];

/** Route segment names (basename-safe) — `p.startsWith("/released-revision/")` fails when the app uses a basename (e.g. /ri-doc-front/...). */
const REVISION_ROUTE_SEGMENT_MARKERS = [
  "edit-procedure-revision",
  "revision-submission",
  "revision",
  "view-revision",
  "view-section",
  "view-step",
  "view-substep",
  "view-childnode",
  "approve-revision",
  "release-revision",
  "released-revision",
  "rejected-revision",
  "archived-revision",
  "add-new-section",
  "edit-section",
  "add-new-step",
  "edit-step",
  "add-new-substep",
  "edit-substep",
  "add-new-attachment",
  "edit-attachment",
  "add-new-video",
  "edit-video",
  "add-new-richtext",
  "edit-rich-text",
  "add-new-canvas",
  "edit-canvas-new",
  "edit-canvas",
  "canvas",
];

function pathnameHasRevisionRouteSegment(pathname) {
  if (!pathname || typeof pathname !== "string") return false;
  const p = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const segments = p.split("/").filter(Boolean);
  return REVISION_ROUTE_SEGMENT_MARKERS.some((m) => segments.includes(m));
}

export function isRevisionRelatedPath(pathname) {
  if (!pathname || typeof pathname !== "string") return false;
  const p = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const segments = p.split("/").filter(Boolean);
  if (segments.includes("edit-procedure-revision")) return true;
  if (pathnameHasRevisionRouteSegment(pathname)) return true;
  return REVISION_PATH_PREFIXES.some((prefix) => p.startsWith(prefix));
}

/**
 * True when we have clearly left revision (so we can clear sticky ref).
 * We only clear when pathname is a settled non-revision path (length > 1) to avoid clearing
 * during brief empty or "/" during route transitions.
 */
export function isDefinitelyNonRevisionPath(pathname) {
  if (pathname == null || typeof pathname !== "string") return true;
  const p = pathname.trim();
  if (p.length <= 1) return false;
  const normalized = p.startsWith("/") ? p : `/${p}`;
  const segments = normalized.split("/").filter(Boolean);
  // Router basename (e.g. /ri-doc-front/...) — segment-based check
  if (segments.includes("edit-procedure-revision")) return false;
  if (pathnameHasRevisionRouteSegment(pathname)) return false;
  // /canvas-templates and /add-canvas-template are not revision-edit flows; treat as non-revision
  if (normalized === "/canvas-templates" || normalized.startsWith("/canvas-templates/")) return true;
  if (normalized === "/add-canvas-template" || normalized.startsWith("/add-canvas-template/")) return true;
  return !REVISION_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

/**
 * Get revision ID from the current pathname for revision-scoped tab lock.
 * Returns null if the path is not a revision-related route.
 */
export function getRevisionIdFromPath(pathname) {
  if (!pathname || typeof pathname !== "string") return null;
  const p = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const segments = p.split("/").filter(Boolean);

  // /.../edit-procedure-revision/:revisionId (basename-safe, e.g. /ri-doc-front/edit-procedure-revision/:id)
  const epIdx = segments.indexOf("edit-procedure-revision");
  if (epIdx >= 0 && segments.length > epIdx + 1) {
    return segments[epIdx + 1];
  }

  // /.../revision-submission/partId/revision/type/partNumber/revisionId/description (basename-safe)
  const rsIdx = segments.indexOf("revision-submission");
  if (rsIdx >= 0 && segments.length > rsIdx + 5) {
    return segments[rsIdx + 5];
  }
  // /.../revision/partId/revisionId — not revision-submission (segment is "revision" only)
  const revIdx = segments.indexOf("revision");
  if (
    revIdx >= 0 &&
    segments[revIdx] === "revision" &&
    segments.length > revIdx + 2
  ) {
    return segments[revIdx + 2];
  }
  // /.../view-revision/part_id/revision_id
  const vrIdx = segments.indexOf("view-revision");
  if (vrIdx >= 0 && segments.length > vrIdx + 2) {
    return segments[vrIdx + 2];
  }
  // /view-section/.../partId/revisionId/section_id_ref
  const vsIdx = segments.indexOf("view-section");
  if (vsIdx >= 0 && segments.length > vsIdx + 5) {
    return segments[vsIdx + 4];
  }
  // /view-step/.../revisionId/section_id_ref/step_id_ref
  const vstIdx = segments.indexOf("view-step");
  if (vstIdx >= 0 && segments.length > vstIdx + 5) {
    return segments[vstIdx + 4];
  }
  // /view-substep/.../revisionId/.../substep_id_ref
  const vsubIdx = segments.indexOf("view-substep");
  if (vsubIdx >= 0 && segments.length > vsubIdx + 6) {
    return segments[vsubIdx + 4];
  }
  // /view-childnode/.../revisionId/.../child_node_id
  const vcnIdx = segments.indexOf("view-childnode");
  if (vcnIdx >= 0 && segments.length > vcnIdx + 5) {
    return segments[vcnIdx + 4];
  }
  // /.../approve-revision/.../revisionId/... — partId, revision, type, partNumber, revisionId, description
  const arIdx = segments.indexOf("approve-revision");
  if (arIdx >= 0 && segments.length > arIdx + 5) {
    return segments[arIdx + 5];
  }
  const lrIdx = segments.indexOf("release-revision");
  if (lrIdx >= 0 && segments.length > lrIdx + 5) {
    return segments[lrIdx + 5];
  }
  // /.../released-revision/.../description/revisionId
  const relIdx = segments.indexOf("released-revision");
  if (relIdx >= 0 && segments.length > relIdx + 6) {
    return segments[relIdx + 6];
  }
  // /.../rejected-revision/.../status/revisionId
  const rejIdx = segments.indexOf("rejected-revision");
  if (rejIdx >= 0 && segments.length > rejIdx + 7) {
    return segments[rejIdx + 7];
  }
  const archIdx = segments.indexOf("archived-revision");
  if (archIdx >= 0 && segments.length > archIdx + 7) {
    return segments[archIdx + 7];
  }
  // add-new-section, edit-section, add-new-step, etc.
  if (p.includes("/add-new-section/") || p.includes("/edit-section/")) return segments[3] ?? null;
  if (p.includes("/add-new-step/") || p.includes("/edit-step/")) return segments[4] ?? null;
  if (p.includes("/add-new-substep/") || p.includes("/edit-substep/")) return segments[5] ?? null;
  if (p.includes("/add-new-attachment/") || p.includes("/edit-attachment/")) return segments[5] ?? null;
  if (p.includes("/add-new-video/") || p.includes("/edit-video/")) return segments[5] ?? null;
  if (p.includes("/add-new-richtext/") || p.includes("/edit-rich-text/")) return segments[5] ?? null;
  if (p.includes("/add-new-canvas/") || p.includes("/edit-canvas-new/")) return segments[5] ?? null;
  // /edit-canvas/:sectionId/:partId/:revisionId/... (basename-safe)
  const ecIdx = segments.indexOf("edit-canvas");
  if (ecIdx >= 0 && segments.length > ecIdx + 3) {
    return segments[ecIdx + 3] ?? null;
  }

  return null;
}
